import React from "react";
import { Link } from "react-router-dom";
import CategoryCard from "../product/CategoryCard";
import ProductCard from "../product/ProductCard";
import LoadingState from "../shared/LoadingState";
import { Banner, BrandLogo, CategorySummary, Product } from "../../types/store";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { useProgressiveProductReveal } from "../../hooks/useProgressiveProductReveal";
import heroBanner from "../../assets/banners/hero-laptop-banner.png";
import bulkBanner from "../../assets/offers/cashback.png";
import walletIcon from "../../assets/wallet-laptop.png";
import electricianImage from "../../assets/services/electrician.webp";
import WhyShopSection from "./WhyShopSection";
import OfferBannerStrip from "./OfferBannerStrip";

export interface DesktopHomeSection {
  sectionKey: string;
  title: string;
  eyebrow?: string;
  products: Product[];
  seeAllTo: string;
  kind?: "products" | "categories" | "brands" | "offer-cards" | "offer-banners";
}

interface DesktopHomePageProps {
  categories: CategorySummary[];
  brands: BrandLogo[];
  sections: DesktopHomeSection[];
  offerCards: Banner[];
  offerBanners: Banner[];
  walletBalance: number;
  recentWalletTransaction?: { type: "CREDIT" | "DEBIT" | "REFUND"; amount: number; description: string } | null;
  loading: boolean;
}

const DesktopProductShelf: React.FC<{
  title: string;
  products: Product[];
  seeAllTo: string;
  loading: boolean;
}> = ({ title, products, seeAllTo, loading }) => {
  const { handleScroll, visibleCount, viewportRef } = useProgressiveProductReveal(products.length);
  const visibleProducts = products.slice(0, visibleCount);

  return (
    <section className="desktop-storefront__section desktop-product-shelf">
      <header className="desktop-storefront__section-heading">
        <h2>{title}</h2>
        <Link to={seeAllTo}>View All</Link>
      </header>
      {loading ? (
        <LoadingState />
      ) : visibleProducts.length ? (
        <div className="desktop-product-shelf__viewport" ref={viewportRef} onScroll={handleScroll}>
          <div className="desktop-product-grid">
            {visibleProducts.map((product) => (
              <ProductCard key={product.id} product={product} compact />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

const DesktopHomePage: React.FC<DesktopHomePageProps> = ({
  categories,
  brands,
  sections,
  offerCards,
  offerBanners,
  walletBalance,
  recentWalletTransaction,
  loading,
}) => {
  const desktopCategories = categories
    .filter((category) => category.slug !== "services" && category.name.toLowerCase() !== "services")
    .slice(0, 8);

  return (
    <div className="desktop-storefront">
      <section className="desktop-storefront__hero">
        <img src={heroBanner} alt="Everything you need, all in one place" />
        <div className="desktop-storefront__hero-actions">
          <Link className="is-primary" to="/products?discover=1&view=collection">
            <span className="desktop-storefront__hero-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M6 6h15l-1.8 8.5a2 2 0 0 1-2 1.5H9.2A2 2 0 0 1 7.2 14L6 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <circle cx="10" cy="19" r="1.6" fill="currentColor" />
                <circle cx="18" cy="19" r="1.6" fill="currentColor" />
              </svg>
            </span>
            <span>Shop Products</span>
          </Link>
          <Link to="/services">
            <span className="desktop-storefront__hero-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M7 20V8h10v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 10h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M9 8a3 3 0 1 1 6 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
            <span>Book a Service</span>
          </Link>
          <Link className="is-dark" to="/bulk-order">
            <span className="desktop-storefront__hero-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 7h12l4 5v5H4V7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M16 7v5h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <circle cx="8" cy="18" r="1.6" fill="currentColor" />
                <circle cx="17" cy="18" r="1.6" fill="currentColor" />
              </svg>
            </span>
            <span>Bulk Orders</span>
          </Link>
        </div>
      </section>

      <section className="desktop-storefront__section desktop-benefit-row">
        <Link className="desktop-benefit-card desktop-benefit-card--wallet" to="/wallet">
          <div>
            <small>WALLET BALANCE</small>
            <strong>₹{walletBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
            <span>
              {recentWalletTransaction
                ? `${recentWalletTransaction.type === "DEBIT" ? "-" : "+"}₹${Math.abs(
                    recentWalletTransaction.amount,
                  ).toLocaleString("en-IN", { minimumFractionDigits: 2 })} ${recentWalletTransaction.description}`
                : "No recent wallet transaction"}
            </span>
            <b>View Wallet</b>
          </div>
          <img src={walletIcon} alt="" />
        </Link>
        <Link className="desktop-benefit-card desktop-benefit-card--services" to="/services">
          <div><small>Book Services</small><span>Schedule electrician, plumber, carpenter, painter, and appliance repair support.</span><b>View Services</b></div>
          <img src={electricianImage} alt="Services" />
        </Link>
        <Link className="desktop-benefit-card desktop-benefit-card--bulk" to="/bulk-order">
          <div><small>Bulk Orders</small><span>Get better prices for large requirements.</span><b>Request Bulk Quote</b></div>
          <img src={bulkBanner} alt="Bulk order boxes" />
        </Link>
      </section>

      {sections.map((section) => {
        if (section.kind === "categories") {
          return (
            <section key={section.sectionKey} className="desktop-storefront__section desktop-category-ribbon" id={section.sectionKey}>
              <header className="desktop-storefront__section-heading">
                <h2>{section.title}</h2>
                <Link to="/categories">View All</Link>
              </header>
              <div className="desktop-category-grid">
                {desktopCategories.map((category) => (
                  <CategoryCard key={category.id || category.name} category={category} />
                ))}
              </div>
            </section>
          );
        }

        if (section.kind === "brands") {
          return (
            <section key={section.sectionKey} id={section.sectionKey} className="desktop-storefront__section desktop-brand-strip">
              <header className="desktop-storefront__section-heading">
                <h2>{section.title}</h2>
                <Link to="/products?discover=1&view=collection">View All</Link>
              </header>
              <div className="desktop-brand-grid">
                {brands.slice(0, 8).map((brand) => (
                  <Link
                    key={brand.id}
                    to={`/products?discover=1&view=collection&brand=${encodeURIComponent(brand.brandName)}&title=${encodeURIComponent(brand.brandName)}`}
                  >
                    <img src={resolveMediaUrl(brand.logoUrl)} alt={brand.brandName} />
                  </Link>
                ))}
              </div>
            </section>
          );
        }

        if (section.kind === "offer-cards") {
          return (
            <OfferBannerStrip
              key={section.sectionKey}
              banners={offerCards}
              title={section.title}
              columns={4}
            />
          );
        }

        if (section.kind === "offer-banners") {
          return (
            <OfferBannerStrip
              key={section.sectionKey}
              banners={offerBanners}
              title={section.title}
              columns={3}
            />
          );
        }

        return (
          <DesktopProductShelf
            key={section.sectionKey}
            title={section.title}
            products={section.products}
            seeAllTo={section.seeAllTo}
            loading={loading}
          />
        );
      })}

      <WhyShopSection />
    </div>
  );
};

export default DesktopHomePage;
